import React, { useState, useMemo, useEffect, useCallback } from "react";
import * as THREE from "three";
import { ArrowLeft, ArrowRight, ArrowUp, Network } from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/Collapsible";
import { useBodies, useSceneActions, useSites, useWorldBodies } from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import Separator from "../ui/Separator";
import MenuSelect from "../ui/MenuSelect";
import DragNumberInput from "../ui/DragNumberInput";
import AxisNumberInput from "../ui/AxisNumberInput";
import { SiteVisibilityToolbar } from "../../selected-site-visibility";
import { useInspectorMobileScenePeek } from "./inspectorMobileScenePeekContext";
import { eulerDegreesToQuaternion, quaternionToEulerDegrees } from "../../core/transform";
import OffsetEditor from "./OffsetEditor";
import { getWorldBodyLabelForBodyUuid } from "../../utils/worldBodyLabels";
import { useNavigateToObjectFromBodyUuid } from "../../hooks/useNavigateToObjectFromBodyUuid";
import { getViewerHandles } from "../../runtime/viewerHandles";
import { requestInspectorChromeFlash } from "../../utils/inspectorChromeFlash";
import { getTransformEditorBlockProps, usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import { logger } from "../../utils/logger";
import TransformGizmoHeaderActions from "./TransformGizmoHeaderActions";
import { useTransformGizmoControls } from "./useTransformGizmoControls";
import GripperAttachmentSection from "./GripperAttachmentSection";
import QuickNavSection from "./QuickNavSection";
import InspectorPhysicsLockedCollapsibleWrap from "./InspectorPhysicsLockedCollapsibleWrap";
import { usePanelStore } from "../../stores/panelStore";
import { EntireObjectIcon } from "../ui/Icons";
import Tooltip from "../ui/Tooltip";
import { INSPECTOR_CHROME_SECTION_ICON_CLASS } from "../../utils/inspectorChromeButtonClasses";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";

import {
  getThreeGeomArgsFromMjcfSize,
  getThreeGeomScaleFromMjcfSize,
} from "../../utils/mujocoGeomSizing";

const SITE_KIND_OPTIONS = [
  { value: "sphere", label: "Sphere" },
  { value: "box", label: "Box" },
  { value: "cylinder", label: "Cylinder" },
  { value: "capsule", label: "Capsule" },
  { value: "ellipsoid", label: "Ellipsoid" },
];

const SITE_KIND_LABELS = SITE_KIND_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const SITE_SIZE_FIELDS_BY_KIND = {
  sphere: [{ index: 0, label: "Radius" }],
  cylinder: [
    { index: 0, label: "Radius" },
    { index: 1, label: "Height" },
  ],
  capsule: [
    { index: 0, label: "Radius" },
    { index: 1, label: "Length" },
  ],
};

const SITE_KIND_HELP_TEXT = {
  sphere: "MuJoCo stores sphere site size as radius.",
  cylinder: "MuJoCo stores cylinder height as half-length along the local Z axis.",
  capsule: "MuJoCo stores capsule length as half-length of the straight section.",
  box: "MuJoCo stores box site size as X/Y/Z half-extents.",
  ellipsoid: "MuJoCo stores ellipsoid site size as X/Y/Z radii.",
};

const normalizeSiteSize = (size) => {
  const source = Array.isArray(size) ? size : [];
  return [0, 1, 2].map((index) => {
    const value = Number(source[index]);
    return Number.isFinite(value) && value > 0 ? value : 0.02;
  });
};

const deriveSiteSizeForKind = (kind, currentSize) => {
  const size = normalizeSiteSize(currentSize);
  const radius = size[0] || 0.02;
  switch (kind) {
    case "sphere":
      return [radius, radius, radius];
    case "cylinder":
    case "capsule":
      return [radius, size[1] || radius, size[1] || radius];
    case "box":
    case "ellipsoid":
      return size;
    default:
      return [radius, radius, radius];
  }
};

const createSiteMarkerGeometry = (kind, size) => {
  switch (kind) {
    case "box":
      return new THREE.BoxGeometry(...getThreeGeomArgsFromMjcfSize("box", size));
    case "cylinder":
      return new THREE.CylinderGeometry(...getThreeGeomArgsFromMjcfSize("cylinder", size));
    case "capsule":
      return new THREE.CapsuleGeometry(...getThreeGeomArgsFromMjcfSize("capsule", size));
    case "ellipsoid":
      return new THREE.SphereGeometry(...getThreeGeomArgsFromMjcfSize("ellipsoid", size));
    case "sphere":
    default:
      return new THREE.SphereGeometry(...getThreeGeomArgsFromMjcfSize("sphere", size));
  }
};

/**
 * Quick tree nav for site inspector — back/forward, parent body/object, scene graph header (matches collision geom panel).
 */
const _SiteQuickNavSection = React.memo(function SiteQuickNavSection({
  quickNavBackTarget = null,
  onQuickNavBack = null,
  quickNavForwardTarget = null,
  onQuickNavForward = null,
  parentBodyUuid,
  parentBodyName,
  objectLabel,
  onNavigateToObject,
}) {
  const [activeSubmenuKey, setActiveSubmenuKey] = useState(null);
  const setSceneGraphOpen = usePanelStore((s) => s.actions.setSceneGraphOpen);

  const canGoBack = !!quickNavBackTarget && typeof onQuickNavBack === "function";
  const canGoForward = !!quickNavForwardTarget && typeof onQuickNavForward === "function";

  const navigateToParentBody = useCallback(() => {
    if (!parentBodyUuid) return;
    const selection = useSelectionStore.getState();
    selection.setLastSelectionSource("sceneTree");
    if (selection.selectedBody !== parentBodyUuid) {
      selection.selectBody(parentBodyUuid);
    }
    requestInspectorChromeFlash();
  }, [parentBodyUuid]);

  useEffect(() => {
    const isEditableTarget = (target) => {
      if (!target || typeof target.closest !== "function") return false;
      return Boolean(
        target.closest("input, textarea, select, [contenteditable], [role='textbox']")
      );
    };

    const handleAltArrowQuickNav = (event) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (isEditableTarget(event.target)) return;

      if (event.key === "ArrowLeft" && canGoBack) {
        event.preventDefault();
        event.stopPropagation();
        onQuickNavBack(quickNavBackTarget);
        return;
      }
      if (event.key === "ArrowRight" && canGoForward) {
        event.preventDefault();
        event.stopPropagation();
        onQuickNavForward(quickNavForwardTarget);
        return;
      }
      if (event.key === "ArrowUp" && parentBodyUuid) {
        event.preventDefault();
        event.stopPropagation();
        navigateToParentBody();
      }
    };

    window.addEventListener("keydown", handleAltArrowQuickNav, true);
    return () => window.removeEventListener("keydown", handleAltArrowQuickNav, true);
  }, [
    parentBodyUuid,
    canGoBack,
    canGoForward,
    navigateToParentBody,
    onQuickNavBack,
    onQuickNavForward,
    quickNavBackTarget,
    quickNavForwardTarget,
  ]);

  const backLabel = canGoBack ? `Back (ALT + ←): ${quickNavBackTarget.label}` : "Back (ALT + ←)";
  const forwardLabel = canGoForward
    ? `Forward (ALT + →): ${quickNavForwardTarget.label}`
    : "Forward (ALT + →)";
  const parentBodyLabel = parentBodyUuid
    ? `Parent Body (ALT + ↑): ${stripPrefix(parentBodyName) || parentBodyUuid}`
    : "Parent Body (ALT + ↑)";
  const objectLabelStripped = objectLabel ? stripPrefix(objectLabel) : null;
  const inspectParentObjectLabel = objectLabelStripped
    ? `Inspect Parent Object: ${objectLabelStripped}`
    : "Inspect Parent Object";
  const canNavigateToObject = !!parentBodyUuid && typeof onNavigateToObject === "function";

  const quickNavActions = [
    {
      key: "back",
      label: backLabel,
      icon: <ArrowLeft className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoBack,
      onDefaultClick: () => {
        if (canGoBack) onQuickNavBack(quickNavBackTarget);
      },
    },
    {
      key: "forward",
      label: forwardLabel,
      icon: <ArrowRight className="w-4 h-4" aria-hidden="true" />,
      disabled: !canGoForward,
      onDefaultClick: () => {
        if (canGoForward) onQuickNavForward(quickNavForwardTarget);
      },
    },
    {
      key: "parentBody",
      label: parentBodyLabel,
      icon: <ArrowUp className="w-4 h-4" aria-hidden="true" />,
      disabled: !parentBodyUuid,
      onDefaultClick: navigateToParentBody,
    },
    {
      key: "parentObject",
      label: inspectParentObjectLabel,
      icon: <EntireObjectIcon className="w-4 h-4" aria-hidden="true" />,
      disabled: !canNavigateToObject,
      onDefaultClick: () => {
        if (canNavigateToObject) onNavigateToObject();
      },
    },
  ];

  return (
    <QuickNavSection
      activeSubmenuKey={activeSubmenuKey}
      setActiveSubmenuKey={setActiveSubmenuKey}
      submenuClassName="absolute left-0 top-full mt-1 min-w-[180px] rounded-md border border-gray-600 p-0.5 shadow-xl z-[10061]"
      submenuStyle={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
      actions={quickNavActions}
      headerActions={
        <Tooltip content="Open Scene Graph">
          <button
            type="button"
            aria-label="Open Scene Graph"
            onClick={() => setSceneGraphOpen(true)}
            className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
            style={{ opacity: 0.55 }}
          >
            <Network size={16} aria-hidden="true" />
          </button>
        </Tooltip>
      }
    />
  );
});

const SitePropertiesPanel = () => {
  const selectedSite = useSelectionStore((state) => state.selectedSite);
  const bodies = useBodies();
  const sites = useSites();
  const worldBodies = useWorldBodies();
  const robotActions = useSceneActions();
  const [editingSiteSizeInputs, setEditingSiteSizeInputs] = useState({});
  const authoringLocked = usePhysicsAuthoringLock();
  const transformEditorBlock = getTransformEditorBlockProps(authoringLocked);
  const { showPositionGizmo, showRotationGizmo, togglePositionGizmo, toggleRotationGizmo } =
    useTransformGizmoControls({
      disabled: transformEditorBlock.disabled,
      enableShortcuts: true,
    });
  const inspectorMobileScenePeek = useInspectorMobileScenePeek();
  const findSiteByIdentifier = (identifier) => {
    if (!identifier) return null;
    if (sites[identifier]) return sites[identifier];
    return Object.values(sites).find((site) => site?.name === identifier) || null;
  };

  // Returns { uuid: bodyMapKey, name: bodyName } for the body containing the site
  const findSiteParentByUuid = (targetUuid) => {
    if (!targetUuid) return null;
    // Search through all bodies to find which one contains this site
    for (const [bodyKey, body] of Object.entries(bodies)) {
      const siteList = Array.isArray(body.sites) ? body.sites : [];
      if (siteList.some((uuid) => uuid === targetUuid)) {
        return { uuid: bodyKey, name: body.name };
      }
    }
    return null;
  };

  const siteNode = selectedSite ? findSiteByIdentifier(selectedSite) : null;
  const parentBodyInfo = siteNode ? findSiteParentByUuid(siteNode.uuid) : null;
  const parentBodyUuid = parentBodyInfo?.uuid || null;

  // Find the world body (root robot/gripper) that this site belongs to
  // Takes a body UUID (map key), not a name
  const findWorldBodyForBody = (targetBodyUuid) => {
    if (!targetBodyUuid) return null;

    // Helper to get the display name from a WorldBody entry
    // WorldBody can be either a string (UUID) or an object { uuid, name }
    // We want to prioritize the WorldBody.name (display name like "Object 1") over the body's name
    const getWorldBodyDisplayName = (wb, bodyKey) => {
      if (typeof wb === "object" && wb.name) {
        // WorldBody object has a display name - use it
        return wb.name;
      }
      // Fallback: use the body's name from the bodies map
      const body = bodies[bodyKey];
      return body?.name || bodyKey;
    };

    const targetBody = bodies[targetBodyUuid];
    if (!targetBody) return null;

    // Check if this body is itself a world body (compare against both map keys and worldBody.uuid)
    const worldBodyEntry = worldBodies.find((wb) => {
      const wbUuid = typeof wb === "string" ? wb : wb.uuid;
      // Match against our map key
      return wbUuid === targetBodyUuid || wbUuid === targetBody?.uuid;
    });
    if (worldBodyEntry) {
      // Return the map key (targetBodyUuid) as the uuid, with proper display name
      const wbName = getWorldBodyDisplayName(worldBodyEntry, targetBodyUuid);
      return { uuid: targetBodyUuid, name: wbName };
    }

    // Otherwise, walk up the tree to find the root
    const visited = new Set();
    let currentKey = targetBodyUuid; // This is the map key

    while (currentKey && !visited.has(currentKey)) {
      visited.add(currentKey);

      const currentBody = bodies[currentKey];

      // Check if current is a world body
      const wb = worldBodies.find((wb) => {
        const wbUuid = typeof wb === "string" ? wb : wb.uuid;
        return wbUuid === currentKey || (currentBody && wbUuid === currentBody.uuid);
      });
      if (wb) {
        const wbName = getWorldBodyDisplayName(wb, currentKey);
        // Return the map key as uuid
        return { uuid: currentKey, name: wbName };
      }

      // Find parent by checking which body has this as a child
      let parentKey = null;
      for (const [key, body] of Object.entries(bodies)) {
        if (body && Array.isArray(body.children)) {
          // Children might be stored as UUIDs or as map keys - check both
          const hasChild = body.children.some(
            (childRef) => childRef === currentKey || childRef === currentBody?.uuid
          );
          if (hasChild) {
            parentKey = key;
            break;
          }
        }
      }

      if (!parentKey) break;
      currentKey = parentKey;
    }

    return null;
  };

  // Check if this is an attachment site and find compatible targets
  const isAttachmentSite = siteNode?.name === "attachment_site";
  const currentWorldBody = parentBodyInfo?.uuid ? findWorldBodyForBody(parentBodyInfo.uuid) : null;
  const _objectLabel = useMemo(
    () => getWorldBodyLabelForBodyUuid(parentBodyUuid, bodies, worldBodies),
    [parentBodyUuid, bodies, worldBodies]
  );
  const { onNavigateToObject: _handleNavigateToObjectProperties } =
    useNavigateToObjectFromBodyUuid(parentBodyUuid);

  if (!siteNode) return null;

  const handlePositionChange = (axis, value) => {
    if (!siteNode?.uuid) return;
    const current = siteNode?.position || [0, 0, 0];
    const next = [...current];
    next[axis] = Number.isFinite(value) ? value : next[axis];
    try {
      robotActions.setSitePosition(siteNode.uuid, next);
    } catch (error) {
      logger.warn("[SitePropertiesPanel] Failed to update site position", error);
    }
    applyLiveSitePosition(next);
  };

  const handleGlobalPositionChange = (axis, value) => {
    if (!siteNode?.uuid) return;
    const nextGlobal = [...globalPosition];
    nextGlobal[axis] = Number.isFinite(value) ? value : nextGlobal[axis];

    let nextLocal = null;
    try {
      const siteObj = findLiveSiteObject();
      if (siteObj) {
        const chain = [];
        let cur = siteObj.parent;
        while (cur) {
          chain.push(cur);
          cur = cur.parent;
        }
        const parentWorld = new THREE.Matrix4().identity();
        for (let i = chain.length - 1; i >= 0; i--) {
          const n = chain[i];
          if (n && typeof n.updateMatrix === "function") n.updateMatrix();
          if (n && n.matrix) parentWorld.multiply(n.matrix);
        }
        const invParentWorld = parentWorld.clone();
        invParentWorld.invert();
        const worldPos = new THREE.Vector3(nextGlobal[0], nextGlobal[1], nextGlobal[2]);
        const localPos = worldPos.applyMatrix4(invParentWorld);
        nextLocal = [localPos.x, localPos.y, localPos.z];
      }
    } catch (error) {
      logger.warn("[SitePropertiesPanel] Failed to compute local site position", error);
    }

    if (!nextLocal) nextLocal = [...nextGlobal];
    try {
      robotActions.setSitePosition(siteNode.uuid, nextLocal);
    } catch (error) {
      logger.warn("[SitePropertiesPanel] Failed to update site position", error);
    }
    applyLiveSitePosition(nextLocal);
  };

  const handleRotationChange = (axis, value) => {
    if (!siteNode?.uuid) return;
    const currentDeg = quaternionToEulerDegrees(siteNode?.orientation || [1, 0, 0, 0]);
    const nextDeg = [...currentDeg];
    nextDeg[axis] = Number.isFinite(value) ? value : nextDeg[axis];
    const nextQuat = eulerDegreesToQuaternion(nextDeg);
    try {
      robotActions.setSiteOrientation(siteNode.uuid, nextQuat);
    } catch (error) {
      logger.warn("[SitePropertiesPanel] Failed to update site orientation", error);
    }
    applyLiveSiteOrientation(nextQuat);
  };

  const siteData = {
    position: {
      x: siteNode.position?.[0] || 0,
      y: siteNode.position?.[1] || 0,
      z: siteNode.position?.[2] || 0,
    },
    orientation:
      Array.isArray(siteNode.orientation) && siteNode.orientation.length === 4
        ? siteNode.orientation
        : [1, 0, 0, 0],
  };

  const siteKind = siteNode?.kind || "sphere";
  const siteSize = normalizeSiteSize(siteNode?.size);

  const handleSiteKindChange = (nextKind) => {
    if (!nextKind || nextKind === siteKind) return;
    const nextSize = deriveSiteSizeForKind(nextKind, siteSize);
    robotActions.setSiteKindAndSize(siteNode.uuid, nextKind, nextSize);
    applyLiveSiteShape(nextKind, nextSize);
    setEditingSiteSizeInputs({});
  };

  const handleSiteSizeChange = (index, value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    const nextSize = [...siteSize];
    nextSize[index] = Math.max(0.0001, numeric);
    robotActions.setSiteKindAndSize(siteNode.uuid, siteKind, nextSize);
    applyLiveSiteShape(siteKind, nextSize);
  };

  const findLiveSiteObject = () => {
    try {
      const { scene } = getViewerHandles();
      if (!scene || !siteNode?.uuid) return null;
      let found = null;
      // Avoid recursive traverse() which can overflow the stack if the scene graph is cyclic.
      const stack = [scene];
      const seen = new Set();
      while (stack.length > 0) {
        const obj = stack.pop();
        if (!obj || seen.has(obj)) continue;
        seen.add(obj);
        if (obj?.userData?.type === "site" && obj?.userData?.siteUuid === siteNode.uuid) {
          found = obj;
          break;
        }
        const children = obj.children;
        if (Array.isArray(children) && children.length > 0) {
          for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
        }
      }
      return found;
    } catch {
      return null;
    }
  };

  // The viewer's scene rebuild/sync runs on a coarse schedule; for responsive UI feedback,
  // apply the latest quaternion directly to the live site object (if present).
  const applyLiveSiteOrientation = (quatWxyz) => {
    try {
      const siteObj = findLiveSiteObject();
      if (!siteObj || !Array.isArray(quatWxyz) || quatWxyz.length !== 4) return;
      const w = Number(quatWxyz[0]);
      const x = Number(quatWxyz[1]);
      const y = Number(quatWxyz[2]);
      const z = Number(quatWxyz[3]);
      if (![w, x, y, z].every(Number.isFinite)) return;
      const q = new THREE.Quaternion(x, y, z, w).normalize();
      siteObj.quaternion.copy(q);
      siteObj.updateMatrixWorld(true);
    } catch (error) {
      logger.warn("[SitePropertiesPanel] Failed to update live site orientation", error);
    }
  };

  const applyLiveSitePosition = (posXyz) => {
    try {
      const siteObj = findLiveSiteObject();
      if (!siteObj || !Array.isArray(posXyz) || posXyz.length !== 3) return;
      const x = Number(posXyz[0]);
      const y = Number(posXyz[1]);
      const z = Number(posXyz[2]);
      if (![x, y, z].every(Number.isFinite)) return;
      siteObj.position.set(x, y, z);
      siteObj.updateMatrixWorld(true);
    } catch (error) {
      logger.warn("[SitePropertiesPanel] Failed to update live site position", error);
    }
  };

  const applyLiveSiteShape = (kind, size) => {
    try {
      const siteObj = findLiveSiteObject();
      if (!siteObj) return;
      let siteMesh = siteObj.isMesh ? siteObj : null;
      if (!siteMesh && Array.isArray(siteObj.children)) {
        siteMesh =
          siteObj.children.find((child) => child?.isMesh && child?.userData?.type === "site") ||
          null;
      }
      if (!siteMesh) return;
      const nextKind = SITE_KIND_LABELS[kind] ? kind : "sphere";
      const nextSize = normalizeSiteSize(size);
      const previousGeometry = siteMesh.geometry;
      siteMesh.geometry = createSiteMarkerGeometry(nextKind, nextSize);
      if (nextKind === "ellipsoid") {
        siteMesh.scale.set(...getThreeGeomScaleFromMjcfSize("ellipsoid", nextSize));
      } else {
        siteMesh.scale.set(1, 1, 1);
      }
      siteMesh.geometry.computeBoundingSphere?.();
      siteMesh.geometry.computeBoundingBox?.();
      siteMesh.updateMatrixWorld(true);
      previousGeometry?.dispose?.();
    } catch (error) {
      logger.warn("[SitePropertiesPanel] Failed to update live site shape", error);
    }
  };

  const localPosition = [siteData.position.x, siteData.position.y, siteData.position.z];
  const localRotationDeg = quaternionToEulerDegrees(siteData.orientation);
  const globalPosition = (() => {
    try {
      const siteObj = findLiveSiteObject();
      if (siteObj) {
        siteObj.updateMatrixWorld(true);
        const p = new THREE.Vector3();
        siteObj.getWorldPosition(p);
        return [p.x, p.y, p.z];
      }
    } catch (error) {
      logger.warn("[SitePropertiesPanel] Failed to read live site position", error);
    }
    return [...localPosition];
  })();

  const renderSiteTypeSelect = () => (
    <div className="space-y-1">
      <label
        className="text-xs text-gray-400 select-none inline-flex items-center gap-1.5"
        htmlFor={`site-type-${siteNode.uuid}`}
      >
        Type
      </label>
      <MenuSelect
        size="compact"
        value={siteKind}
        onChange={handleSiteKindChange}
        disabled={transformEditorBlock.disabled}
        options={[
          ...(!SITE_KIND_LABELS[siteKind]
            ? [{ value: siteKind, label: siteKind || "Unknown" }]
            : []),
          ...SITE_KIND_OPTIONS,
        ]}
        triggerProps={{
          id: `site-type-${siteNode.uuid}`,
          title: transformEditorBlock.disabled ? transformEditorBlock.title : undefined,
          onPointerDown: (event) => event.stopPropagation(),
        }}
        aria-label="Site type"
      />
    </div>
  );

  const renderSiteSizeInput = ({ label, index, axis = null }) => {
    const inputKey = `site-size-${index}`;
    const displayValue =
      editingSiteSizeInputs[inputKey] !== undefined
        ? editingSiteSizeInputs[inputKey]
        : siteSize[index]?.toFixed(3) || "0.020";
    const commonProps = {
      value: displayValue,
      dragValue: siteSize[index],
      deferDragUpdates: false,
      onDragValueChange: (nextValue) => handleSiteSizeChange(index, nextValue),
      onChange: (event) => {
        const value = event.target.value;
        setEditingSiteSizeInputs((prev) => ({ ...prev, [inputKey]: value }));
        const numeric = Number.parseFloat(value);
        if (Number.isFinite(numeric)) handleSiteSizeChange(index, numeric);
      },
      onFocus: (event) => {
        setEditingSiteSizeInputs((prev) => ({
          ...prev,
          [inputKey]: event.target.value,
        }));
      },
      onBlur: (event) => {
        const numeric = Number.parseFloat(event.target.value);
        if (Number.isFinite(numeric)) handleSiteSizeChange(index, numeric);
        setEditingSiteSizeInputs((prev) => {
          const next = { ...prev };
          delete next[inputKey];
          return next;
        });
      },
      disabled: transformEditorBlock.disabled,
      inputClassName:
        "w-full px-2 py-1 text-xs border border-gray-600 rounded bg-gray-800 text-gray-100",
      inputProps: { min: "0.0001", step: "0.01" },
      tooltipContent: transformEditorBlock.disabled ? transformEditorBlock.title : null,
    };

    if (axis) {
      return (
        <AxisNumberInput
          axis={axis}
          {...commonProps}
          labelClassName="text-xs text-gray-300 w-3 text-center"
        />
      );
    }

    return (
      <DragNumberInput
        label={label}
        {...commonProps}
        wrapperClassName="flex items-center gap-1"
        labelClassName="text-xs text-gray-300 select-none shrink-0"
        inputClassName="min-w-0 flex-1 px-2 py-1 text-xs border border-gray-600 rounded bg-gray-800 text-gray-100"
      />
    );
  };

  const renderSiteSizeInputs = () => {
    const helpText = SITE_KIND_HELP_TEXT[siteKind];

    if (siteKind === "sphere") {
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1 col-span-1">
              {renderSiteSizeInput({ label: "Radius", index: 0 })}
            </div>
          </div>
          {helpText ? <div className="text-xs panel-text-helper">{helpText}</div> : null}
        </div>
      );
    }

    if (siteKind === "cylinder" || siteKind === "capsule") {
      const fields = SITE_SIZE_FIELDS_BY_KIND[siteKind];
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            {fields.map(({ label, index }) => (
              <div key={label} className="space-y-1 col-span-1">
                {renderSiteSizeInput({ label, index })}
              </div>
            ))}
          </div>
          {helpText ? <div className="text-xs panel-text-helper">{helpText}</div> : null}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-2">
          {["X", "Y", "Z"].map((axis, index) => (
            <div key={axis} className="space-y-1">
              {renderSiteSizeInput({ axis, index })}
            </div>
          ))}
        </div>
        {helpText ? <div className="text-xs panel-text-helper">{helpText}</div> : null}
      </div>
    );
  };

  if (inspectorMobileScenePeek?.compact) {
    return (
      <div className="p-3 space-y-4">
        <SiteVisibilityToolbar siteUuid={siteNode.uuid} />
      </div>
    );
  }

  return (
    <div className="p-3 space-y-4">
      <SiteVisibilityToolbar siteUuid={siteNode.uuid} />

      <Separator />

      {isAttachmentSite && currentWorldBody?.uuid && (
        <GripperAttachmentSection
          worldBodyMapKey={currentWorldBody.uuid}
          preferredGripperSiteUuid={siteNode.uuid}
          physicsLocked={authoringLocked}
        />
      )}

      <InspectorPhysicsLockedCollapsibleWrap locked={authoringLocked}>
        <Collapsible defaultOpen>
          <CollapsibleTrigger quiet aria-label="Toggle Site Shape">
            <span className="min-w-0 truncate text-left">Shape</span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="pt-2 space-y-3">
              {renderSiteTypeSelect()}
              {renderSiteSizeInputs()}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </InspectorPhysicsLockedCollapsibleWrap>

      <InspectorPhysicsLockedCollapsibleWrap locked={authoringLocked}>
        <Collapsible defaultOpen>
          <CollapsibleTrigger
            quiet
            aria-label="Toggle Transform"
            actions={
              <TransformGizmoHeaderActions
                disabled={transformEditorBlock.disabled}
                disabledTitle={transformEditorBlock.title}
                showPositionGizmo={showPositionGizmo}
                showRotationGizmo={showRotationGizmo}
                onTogglePositionGizmo={togglePositionGizmo}
                onToggleRotationGizmo={toggleRotationGizmo}
              />
            }
          >
            <span className="min-w-0 truncate text-left">Transform</span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="pt-2">
              <OffsetEditor
                embedded
                title="Site Transform"
                globalPosition={globalPosition}
                localPosition={localPosition}
                localRotationDeg={localRotationDeg}
                quaternion={siteData.orientation}
                onGlobalPositionChange={handleGlobalPositionChange}
                onPositionChange={handlePositionChange}
                onRotationChange={handleRotationChange}
                disabled={transformEditorBlock.disabled}
                disabledTitle={transformEditorBlock.title}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </InspectorPhysicsLockedCollapsibleWrap>
    </div>
  );
};

export default React.memo(SitePropertiesPanel);
